/**
 * Post-login redirect, e.g. send shoppers from the Pre-Owned Books page to /cart after
 * they sign in or sign up.
 *
 * The target travels in the auth URLs (`/login?redirect=/cart`) and is also kept in
 * sessionStorage, so it survives any hop that drops the query string. The stored value
 * expires after REDIRECT_TTL_MS and is cleared once the target page is reached.
 *
 * Only same-site paths are honoured ("/cart", not "//evil.com" or "https://…"),
 * so it can't be used as an open redirect. Falls back to the homepage.
 */
export const REDIRECT_PARAM = "redirect";

const STORAGE_KEY = "postLoginRedirect";
const REDIRECT_TTL_MS = 30 * 60 * 1000;

const isSafePath = (target: string | null | undefined): target is string =>
  !!target && target.startsWith("/") && !target.startsWith("//") && !target.includes("\\");

const readStored = (): string | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { target, savedAt } = JSON.parse(raw);
    if (Date.now() - savedAt > REDIRECT_TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return isSafePath(target) ? target : null;
  } catch {
    return null;
  }
};

const readTarget = (search: string): string | null => {
  const fromUrl = new URLSearchParams(search).get(REDIRECT_PARAM);
  return isSafePath(fromUrl) ? fromUrl : readStored();
};

export const getPostLoginRedirect = (search: string, fallback = "/"): string =>
  readTarget(search) ?? fallback;

/** Appends the current redirect (if any) to another auth path, e.g. "/otp-login?sent=1". */
export const withPostLoginRedirect = (path: string, search: string): string => {
  const target = readTarget(search);
  if (!target) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}${REDIRECT_PARAM}=${encodeURIComponent(target)}`;
};

/** Login URL that returns to `target` afterwards; also remembers it for the session. */
export const loginUrlWithRedirect = (target: string): string => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ target, savedAt: Date.now() }));
  } catch {
    // storage unavailable — the URL param still carries it
  }
  return `/login?${REDIRECT_PARAM}=${encodeURIComponent(target)}`;
};

/** Clears the remembered redirect once the user has landed on it. */
export const clearPostLoginRedirectIfReached = (pathname: string): void => {
  const stored = readStored();
  if (stored && stored.split("?")[0] === pathname) {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
};
